import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Card, Button, Input, Select, useBestaetigung } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'
import { CheckCircle2, AlertTriangle, XCircle, Plus, Trash2 } from 'lucide-react'

// Detailansicht eines Akkus: Alter, Zustand und Wartung aus den DJI-Flugdaten (akku_flugdaten) und dem
// Wartungsbuch (akku_wartungen). Zustand und Fälligkeiten berechnet die Admin-API (akku_historie.bewertung).

export interface AkkuHistorie {
  id: number
  akku_typ_id: number | null
  anzahl_ladezyklen: number | null
  letzte_wartung: string | null
  firmware_version: string | null
  hv_lagertage: number | null
  inbetriebnahme: string | null
  zuletzt_gesehen: string | null
  alter_monate: number | null
  fluege: number
  flugzeit_min: number
  verbrauch_trend_prozent: number | null
  zustand: 'gut' | 'wartung' | 'pruefen'
  zustand_gruende: string[]
  wartung_faellig_am: string | null
  wartung_faellig_zyklen: number | null
  max_ladezyklen: number | null
}

export interface Wartungsregeln {
  id: number
  max_ladezyklen?: number | null
  wartung_zyklen?: number | null
  wartung_monate?: number | null
  hv_warn_tage?: number | null
}

interface Flugdaten {
  id: number
  flug_id: number
  flug_nr: string | null
  drohne: string | null
  start: string | null
  dauer_min: number | null
  start_prozent: number | null
  end_prozent: number | null
  verbrauch_prozent: number | null
  prozent_pro_min: number | null
  min_spannung_v: number | null
  end_spannung_v: number | null
  temp_min: number | null
  temp_max: number | null
  ladezyklen: number | null
}

interface Wartung {
  id: number
  datum: string
  art: string
  art_label: string
  notiz: string | null
  ladezyklen: number | null
  erstellt_von: string | null
}

const datum = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString('de-DE') : '—')
const zahl = (v: number | null | undefined, stellen = 0) =>
  v == null ? '—' : v.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen })
const dauer = (min: number) => `${Math.floor(min / 60)}:${String(Math.round(min % 60)).padStart(2, '0')} h`

/* -------------------------------------------------------------- Zustand */

const ZUSTAND = {
  gut: { label: 'gut', Icon: CheckCircle2, klasse: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30' },
  wartung: { label: 'Wartung fällig', Icon: AlertTriangle, klasse: 'text-amber-200 bg-amber-500/15 border-amber-500/30' },
  pruefen: { label: 'prüfen', Icon: XCircle, klasse: 'text-red-200 bg-red-500/15 border-red-500/30' },
} as const

/** Ampel mit Symbol und Text (nie nur Farbe); die Gründe stehen im Tooltip. */
export function ZustandBadge({ zustand, gruende }: { zustand: AkkuHistorie['zustand']; gruende: string[] }) {
  const z = ZUSTAND[zustand] ?? ZUSTAND.gut
  return (
    <span title={gruende.length ? gruende.join('\n') : 'Keine Auffälligkeiten'}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${z.klasse}`}>
      <z.Icon className="h-3.5 w-3.5" aria-hidden />{z.label}
    </span>
  )
}

/** Ladezyklen mit Balken bis zur Grenze des Akku-Typs. */
export function ZyklenBalken({ zyklen, max }: { zyklen: number | null; max: number | null }) {
  if (zyklen == null) return <span className="text-dark-text-subtle">—</span>
  if (!max) return <span className="tabular-nums">{zyklen}</span>
  const anteil = Math.min(1, zyklen / max)
  return (
    <span className="inline-flex items-center gap-2" title={`${zyklen} von ${max} Ladezyklen`}>
      <span className="tabular-nums text-xs">{zyklen} / {max}</span>
      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-dark-bg-tertiary">
        <span className={`block h-full rounded-full ${anteil >= 1 ? 'bg-red-400' : anteil >= 0.9 ? 'bg-amber-400' : 'bg-primary-400'}`}
          style={{ width: `${anteil * 100}%` }} />
      </span>
    </span>
  )
}

/* ------------------------------------------------------- Verlaufskurve */

/** Eine Kurve je Diagramm (keine zweite Achse), Hover zeigt Datum und Wert. Tabellenansicht: Reiter „Flüge“. */
function Verlauf({ titel, einheit, stellen, punkte }: {
  titel: string; einheit: string; stellen: number
  punkte: Array<{ datum: string | null; wert: number; flug: string | null }>
}) {
  const [aktiv, setAktiv] = useState<number | null>(null)
  const B = 560, H = 150, L = 44, R = 12, O = 12, U = 26
  if (punkte.length < 2) {
    return (
      <Card className="p-4">
        <div className="text-sm font-semibold text-dark-text">{titel}</div>
        <p className="mt-2 text-xs text-dark-text-subtle">Noch zu wenige auswertbare Flüge für einen Verlauf.</p>
      </Card>
    )
  }
  const werte = punkte.map(p => p.wert)
  let min = Math.min(...werte), max = Math.max(...werte)
  const rand = (max - min) * 0.15 || Math.abs(max) * 0.05 || 1
  min -= rand; max += rand
  const x = (i: number) => L + (i / (punkte.length - 1)) * (B - L - R)
  const y = (v: number) => O + (1 - (v - min) / (max - min)) * (H - O - U)
  const pfad = punkte.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.wert).toFixed(1)}`).join(' ')
  const ticks = [min + rand, (min + max) / 2, max - rand]
  const a = aktiv != null ? punkte[aktiv] : null

  return (
    <Card className="p-4">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-dark-text">{titel}</span>
        <span className="text-xs text-dark-text-subtle">letzte {punkte.length} Flüge</span>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${B} ${H}`} className="h-auto w-full" role="img" aria-label={`${titel} über ${punkte.length} Flüge`}
          onMouseLeave={() => setAktiv(null)}>
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={L} x2={B - R} y1={y(t)} y2={y(t)} className="stroke-dark-border" strokeWidth={1} />
              <text x={L - 6} y={y(t) + 3.5} textAnchor="end" className="fill-dark-text-subtle" fontSize={10}>{zahl(t, stellen)}</text>
            </g>
          ))}
          <text x={L} y={H - 8} className="fill-dark-text-subtle" fontSize={10}>{datum(punkte[0].datum)}</text>
          <text x={B - R} y={H - 8} textAnchor="end" className="fill-dark-text-subtle" fontSize={10}>{datum(punkte[punkte.length - 1].datum)}</text>
          {aktiv != null && <line x1={x(aktiv)} x2={x(aktiv)} y1={O} y2={H - U} className="stroke-dark-text-subtle" strokeWidth={1} strokeDasharray="3 3" />}
          <path d={pfad} fill="none" className="stroke-primary-400" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {aktiv != null && <circle cx={x(aktiv)} cy={y(punkte[aktiv].wert)} r={4.5} className="fill-primary-400 stroke-dark-bg-secondary" strokeWidth={2} />}
          {/* Trefferflächen breiter als die Linie */}
          {punkte.map((_, i) => (
            <rect key={i} x={x(i) - (B - L - R) / (punkte.length - 1) / 2} y={O} height={H - O - U}
              width={(B - L - R) / (punkte.length - 1)} fill="transparent" onMouseEnter={() => setAktiv(i)} />
          ))}
        </svg>
        {a && aktiv != null && (
          <div className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg border border-dark-border bg-dark-bg-secondary px-2 py-1 text-xs shadow-xl"
            style={{ left: `${(x(aktiv) / B) * 100}%` }}>
            <div className="text-dark-text-muted">{datum(a.datum)}{a.flug ? ` · ${a.flug}` : ''}</div>
            <div className="font-semibold text-dark-text">{zahl(a.wert, stellen)} {einheit}</div>
          </div>
        )}
      </div>
    </Card>
  )
}

/* ---------------------------------------------------------- Kennzahl */

function Bereich({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <Card className="p-4">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">{titel}</div>
      <dl className="space-y-1.5 text-sm">{children}</dl>
    </Card>
  )
}
function Zeile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-dark-text-muted">{label}</dt>
      <dd className="text-right text-dark-text">{children}</dd>
    </div>
  )
}

/* ============================================================ Ansicht */

type Reiter = 'uebersicht' | 'fluege' | 'wartung' | 'stammdaten'

export function AkkuDetail({ akku, regeln, stammdaten, onGeaendert }: {
  akku: AkkuHistorie
  /** Wartungsregeln des Akku-Typs (null = kein Typ zugeordnet) */
  regeln: Wartungsregeln | null
  /** Bisheriges Stammdaten-Formular (nur lesend) */
  stammdaten: ReactNode
  /** Nach Wartungseintrag/Regeländerung: Liste neu laden (Zustand ändert sich) */
  onGeaendert: () => void
}) {
  const { bestaetigen, dialog } = useBestaetigung()
  const [reiter, setReiter] = useState<Reiter>('uebersicht')
  const [fluege, setFluege] = useState<Flugdaten[]>([])
  const [wartungen, setWartungen] = useState<Wartung[]>([])
  const [arten, setArten] = useState<Array<{ value: string; label: string }>>([])
  const [neu, setNeu] = useState({ datum: new Date().toISOString().slice(0, 10), art: 'SICHTPRUEFUNG', notiz: '' })
  const [regelForm, setRegelForm] = useState<Record<string, string>>({})
  const [fehler, setFehler] = useState<string | null>(null)
  const [speichert, setSpeichert] = useState(false)

  const ladeVerlauf = useCallback(() => {
    api.get(`/akkus/${akku.id}/flugdaten`).then(r => setFluege((r.data as any).items ?? [])).catch(() => setFluege([]))
    api.get(`/akkus/${akku.id}/wartungen`).then(r => setWartungen((r.data as any).items ?? [])).catch(() => setWartungen([]))
  }, [akku.id])
  useEffect(() => { ladeVerlauf() }, [ladeVerlauf])
  useEffect(() => {
    api.get('/akkus/wartungsarten').then(r => setArten((r.data as any).items ?? [])).catch(() => {})
  }, [])
  useEffect(() => {
    setRegelForm({
      max_ladezyklen: regeln?.max_ladezyklen != null ? String(regeln.max_ladezyklen) : '',
      wartung_zyklen: regeln?.wartung_zyklen != null ? String(regeln.wartung_zyklen) : '',
      wartung_monate: regeln?.wartung_monate != null ? String(regeln.wartung_monate) : '',
      hv_warn_tage: regeln?.hv_warn_tage != null ? String(regeln.hv_warn_tage) : '',
    })
  }, [regeln])

  // Verlauf chronologisch, letzte 30 Flüge
  const chronologisch = useMemo(() => [...fluege].reverse().slice(-30), [fluege])
  const verbrauch = chronologisch.filter(f => f.prozent_pro_min != null)
    .map(f => ({ datum: f.start, wert: f.prozent_pro_min as number, flug: f.flug_nr }))
  const spannung = chronologisch.filter(f => f.min_spannung_v != null)
    .map(f => ({ datum: f.start, wert: f.min_spannung_v as number, flug: f.flug_nr }))

  async function aktion(fn: () => Promise<unknown>) {
    setSpeichert(true); setFehler(null)
    try {
      await fn()
      ladeVerlauf(); onGeaendert()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen')
    } finally { setSpeichert(false) }
  }

  const reiterListe: Array<[Reiter, string]> = [
    ['uebersicht', 'Übersicht'], ['fluege', `Flüge (${fluege.length})`], ['wartung', `Wartung (${wartungen.length})`], ['stammdaten', 'Stammdaten'],
  ]

  return (
    <div className="space-y-4">
      {dialog}
      {/* Alter · Zustand · Wartung */}
      <div className="grid gap-4 md:grid-cols-3">
        <Bereich titel="Alter">
          <Zeile label="Inbetriebnahme">{datum(akku.inbetriebnahme)}</Zeile>
          <Zeile label="Alter">{akku.alter_monate != null ? `${akku.alter_monate} Monate` : '—'}</Zeile>
          <Zeile label="Flüge (DJI)">{akku.fluege}</Zeile>
          <Zeile label="Flugzeit">{dauer(akku.flugzeit_min)}</Zeile>
          <Zeile label="Zuletzt geflogen">{datum(akku.zuletzt_gesehen)}</Zeile>
        </Bereich>
        <Bereich titel="Zustand">
          <Zeile label="Bewertung"><ZustandBadge zustand={akku.zustand} gruende={akku.zustand_gruende} /></Zeile>
          <Zeile label="Ladezyklen"><ZyklenBalken zyklen={akku.anzahl_ladezyklen} max={akku.max_ladezyklen} /></Zeile>
          <Zeile label="Verbrauchstrend">
            {akku.verbrauch_trend_prozent != null ? `${akku.verbrauch_trend_prozent > 0 ? '+' : ''}${akku.verbrauch_trend_prozent} %` : 'ab 8 Flügen'}
          </Zeile>
          <Zeile label="Voll geladen gelagert">{akku.hv_lagertage != null ? `${akku.hv_lagertage} Tage` : '—'}</Zeile>
          {akku.zustand_gruende.length > 0 && (
            <ul className="mt-2 space-y-0.5 border-t border-dark-border pt-2 text-xs text-dark-text-muted">
              {akku.zustand_gruende.map(g => <li key={g}>• {g}</li>)}
            </ul>
          )}
        </Bereich>
        <Bereich titel="Wartung">
          <Zeile label="Letzte Wartung">{datum(akku.letzte_wartung)}</Zeile>
          <Zeile label="Nächste (Zeit)">{akku.wartung_faellig_am ? datum(akku.wartung_faellig_am) : '—'}</Zeile>
          <Zeile label="Nächste (Zyklen)">{akku.wartung_faellig_zyklen ?? '—'}</Zeile>
          <Zeile label="Firmware">{akku.firmware_version || '—'}</Zeile>
          <div className="pt-2">
            <Button size="sm" variant="secondary" onClick={() => setReiter('wartung')}><Plus className="mr-1 h-4 w-4" />Wartung eintragen</Button>
          </div>
        </Bereich>
      </div>

      {/* Reiter */}
      <div className="flex gap-1 overflow-x-auto border-b border-dark-border" role="tablist">
        {reiterListe.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={reiter === k} onClick={() => setReiter(k)}
            className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${reiter === k
              ? 'border-primary-500 text-dark-text' : 'border-transparent text-dark-text-muted hover:text-dark-text'}`}>
            {label}
          </button>
        ))}
      </div>

      {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-2 text-sm text-red-200">{fehler}</div>}

      {reiter === 'uebersicht' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Verlauf titel="Verbrauch je Flugminute" einheit="%/min" stellen={2} punkte={verbrauch} />
          <Verlauf titel="Niedrigste Spannung im Flug" einheit="V" stellen={2} punkte={spannung} />
          <p className="text-xs text-dark-text-subtle lg:col-span-2">
            Steigt der Verbrauch je Minute über die Zeit, lässt die Kapazität nach. Sinkt die niedrigste Spannung, ist der
            Akku unter Last schwächer. Gewertet werden Flüge ab 3 Minuten und 5 % Verbrauch.
          </p>
        </div>
      )}

      {reiter === 'fluege' && (
        <>
        <ul className="space-y-3 md:hidden">
          {fluege.length === 0 && <li className="py-6 text-center text-sm text-dark-text-subtle">Noch keine Flugdaten von DJI.</li>}
          {fluege.map(f => (
            <li key={f.id} className="rounded-xl border border-dark-border bg-dark-bg-tertiary/30 p-4">
              <div className="text-base font-semibold">{datum(f.start)} <span className="ml-1 font-mono text-xs font-normal text-dark-text-muted">{f.flug_nr || `#${f.flug_id}`}</span></div>
              <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-dark-text-subtle">Drohne</dt><dd>{f.drohne || '—'}</dd>
                <dt className="text-dark-text-subtle">Dauer</dt><dd>{f.dauer_min != null ? `${zahl(f.dauer_min, 0)} min` : '—'}</dd>
                <dt className="text-dark-text-subtle">Start → Landung</dt><dd>{zahl(f.start_prozent)} % → {zahl(f.end_prozent)} %</dd>
                <dt className="text-dark-text-subtle">Verbrauch</dt><dd>{f.prozent_pro_min != null ? `${zahl(f.prozent_pro_min, 2)} %/min` : '—'}</dd>
                <dt className="text-dark-text-subtle">Spannung min</dt><dd>{f.min_spannung_v != null ? `${zahl(f.min_spannung_v, 2)} V` : '—'}</dd>
                <dt className="text-dark-text-subtle">Temperatur</dt><dd>{f.temp_min != null ? `${zahl(f.temp_min)}–${zahl(f.temp_max)} °C` : '—'}</dd>
                <dt className="text-dark-text-subtle">Zyklen</dt><dd>{f.ladezyklen ?? '—'}</dd>
              </dl>
            </li>
          ))}
        </ul>
        <Card className="hidden overflow-x-auto p-0 md:block">
          <table className="w-full text-sm">
            <thead className="bg-dark-bg-tertiary/60 text-left text-xs text-dark-text-muted">
              <tr>
                {['Datum', 'Flug', 'Drohne', 'Dauer', 'Start → Landung', 'Verbrauch', 'Spannung min', 'Temperatur', 'Zyklen'].map(h =>
                  <th key={h} className="whitespace-nowrap px-3 py-2 font-medium">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {fluege.length === 0 && (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-dark-text-subtle">Noch keine Flugdaten von DJI.</td></tr>
              )}
              {fluege.map(f => (
                <tr key={f.id} className="border-t border-dark-border">
                  <td className="whitespace-nowrap px-3 py-2">{datum(f.start)}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{f.flug_nr || `#${f.flug_id}`}</td>
                  <td className="whitespace-nowrap px-3 py-2">{f.drohne || '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{f.dauer_min != null ? `${zahl(f.dauer_min, 0)} min` : '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{zahl(f.start_prozent)} % → {zahl(f.end_prozent)} %</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{f.prozent_pro_min != null ? `${zahl(f.prozent_pro_min, 2)} %/min` : '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{f.min_spannung_v != null ? `${zahl(f.min_spannung_v, 2)} V` : '—'}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                    {f.temp_min != null ? `${zahl(f.temp_min)}–${zahl(f.temp_max)} °C` : '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{f.ladezyklen ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        </>
      )}

      {reiter === 'wartung' && (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card className="p-4">
              <div className="mb-3 text-sm font-semibold text-dark-text">Wartung eintragen</div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="label">Datum</label>
                  <Input type="date" value={neu.datum} onChange={e => setNeu({ ...neu, datum: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Art</label>
                  <Select value={neu.art} onChange={e => setNeu({ ...neu, art: e.target.value })} options={arten} />
                </div>
                <div className="sm:col-span-3">
                  <label className="label">Notiz</label>
                  <Input value={neu.notiz} onChange={e => setNeu({ ...neu, notiz: e.target.value })} placeholder="z. B. Gehäuse ohne Befund, Kontakte gereinigt" />
                </div>
              </div>
              <p className="mt-2 text-xs text-dark-text-subtle">Die aktuellen Ladezyklen ({akku.anzahl_ladezyklen ?? '—'}) werden mitgespeichert. Der neueste Eintrag setzt „Letzte Wartung“.</p>
              <div className="mt-3 flex justify-end">
                <Button disabled={speichert || !neu.datum} onClick={() => aktion(async () => {
                  await api.post(`/akkus/${akku.id}/wartungen`, neu)
                  setNeu({ ...neu, notiz: '' })
                })}><Plus className="mr-1 h-4 w-4" />Eintragen</Button>
              </div>
            </Card>

            <ul className="space-y-3 md:hidden">
              {wartungen.length === 0 && <li className="py-6 text-center text-sm text-dark-text-subtle">Noch keine Wartungseinträge.</li>}
              {wartungen.map(w => (
                <li key={w.id} className="rounded-xl border border-dark-border bg-dark-bg-tertiary/30 p-4">
                  <div className="text-base font-semibold">{datum(w.datum)} · {w.art_label}</div>
                  <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                    <dt className="text-dark-text-subtle">Zyklen</dt><dd>{w.ladezyklen ?? '—'}</dd>
                    <dt className="text-dark-text-subtle">Notiz</dt><dd>{w.notiz || '—'}</dd>
                    <dt className="text-dark-text-subtle">Erfasst von</dt><dd>{w.erstellt_von || '—'}</dd>
                  </dl>
                  <div className="mt-3 flex justify-end border-t border-dark-border pt-3">
                    <button type="button" aria-label={`Wartungseintrag vom ${datum(w.datum)} löschen`}
                      onClick={async () => { if (await bestaetigen({ titel: 'Wartungseintrag löschen?', text: 'Wartungseintrag', objekt: `${w.art_label} vom ${datum(w.datum)}`, hinweis: 'Der Eintrag wird aus dem Wartungsbuch dieses Akkus entfernt. „Letzte Wartung“ und die Wartungs-Fälligkeit werden neu berechnet.' })) aktion(() => api.delete(`/akkus/${akku.id}/wartungen/${w.id}`)) }}
                      className="inline-flex h-12 w-12 items-center justify-center rounded-lg text-red-500 hover:bg-red-400/10">
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <Card className="hidden overflow-x-auto p-0 md:block">
              <table className="w-full text-sm">
                <thead className="bg-dark-bg-tertiary/60 text-left text-xs text-dark-text-muted">
                  <tr>{['Datum', 'Art', 'Zyklen', 'Notiz', 'Erfasst von', ''].map((h, i) => <th key={i} className="px-3 py-2 font-medium">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {wartungen.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-dark-text-subtle">Noch keine Wartungseinträge.</td></tr>
                  )}
                  {wartungen.map(w => (
                    <tr key={w.id} className="border-t border-dark-border">
                      <td className="whitespace-nowrap px-3 py-2">{datum(w.datum)}</td>
                      <td className="px-3 py-2">{w.art_label}</td>
                      <td className="px-3 py-2 tabular-nums">{w.ladezyklen ?? '—'}</td>
                      <td className="px-3 py-2 text-dark-text-muted">{w.notiz || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-dark-text-muted">{w.erstellt_von || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" title="Eintrag löschen" aria-label="Wartungseintrag löschen"
                          onClick={async () => { if (await bestaetigen({ titel: 'Wartungseintrag löschen?', text: 'Wartungseintrag', objekt: `${w.art_label} vom ${datum(w.datum)}`, hinweis: 'Der Eintrag wird aus dem Wartungsbuch dieses Akkus entfernt. „Letzte Wartung“ und die Wartungs-Fälligkeit werden neu berechnet.' })) aktion(() => api.delete(`/akkus/${akku.id}/wartungen/${w.id}`)) }}
                          className="inline-flex h-12 w-12 items-center justify-center rounded-lg text-red-400 hover:bg-red-400/10 md:h-9 md:w-9">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <Card className="p-4">
            <div className="mb-1 text-sm font-semibold text-dark-text">Wartungsregeln des Akku-Typs</div>
            {!regeln ? (
              <p className="text-xs text-dark-text-subtle">Dem Akku ist kein Akku-Typ zugeordnet. Regeln gelten je Typ – Typ unter „Stammdaten“ → Bearbeiten wählen.</p>
            ) : (
              <>
                <p className="mb-3 text-xs text-dark-text-subtle">Gilt für alle Akkus dieses Typs. Leer = Regel aus.</p>
                <div className="space-y-3">
                  {([
                    ['max_ladezyklen', 'Max. Ladezyklen (Lebensdauer)'],
                    ['wartung_zyklen', 'Wartung alle … Ladezyklen'],
                    ['wartung_monate', 'Wartung alle … Monate'],
                    ['hv_warn_tage', 'Warnung ab … Tagen voll geladen (Standard 10)'],
                  ] as const).map(([k, label]) => (
                    <div key={k}>
                      <label className="label">{label}</label>
                      <Input type="number" min={1} value={regelForm[k] ?? ''} onChange={e => setRegelForm({ ...regelForm, [k]: e.target.value })} />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-end">
                  <Button variant="secondary" disabled={speichert}
                    onClick={() => aktion(() => api.put(`/akku-typen/${regeln.id}/wartungsregeln`, regelForm))}>Regeln speichern</Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {reiter === 'stammdaten' && stammdaten}
    </div>
  )
}
